import { SelectionItem, GroupedItem } from '@utils';
import { create } from 'zustand'
import { useLanguage } from '../context/LanguageContext'

// Liste unique et simple des cadres de rencontre (partagée web + mobile).
// Chaque item a son icône pour un rendu plus clair et plus parlant.
const contextItems: SelectionItem[] = [
  { id: 'ecole-universite', name: 'meetingContexts.ecole-universite', icon: 'school' },
  { id: 'travail', name: 'meetingContexts.travail', icon: 'work' },
  { id: 'evenement-pro', name: 'meetingContexts.evenement-pro', icon: 'event' },
  { id: 'evenement-social', name: 'meetingContexts.evenement-social', icon: 'aperitif' },
  { id: 'amis-connaissance', name: 'meetingContexts.amis-connaissance', icon: 'users' },
  { id: 'voyage', name: 'meetingContexts.voyage', icon: 'flight' },
  { id: 'sport-activite', name: 'meetingContexts.sport-activite', icon: 'sport' },
  { id: 'en-ligne', name: 'meetingContexts.en-ligne', icon: 'globe' },
  { id: 'dating', name: 'meetingContexts.dating', icon: 'heart' },
  { id: 'famille', name: 'meetingContexts.famille', icon: 'house' },
]

// Conservé au format "groupé" pour rester compatible avec l'API du store,
// mais volontairement en un seul groupe sans titre : la liste s'affiche à plat.
const contextGrouped: GroupedItem[] = [
  { id: 'meeting-contexts', name: '', items: contextItems },
]

// Correspondance des anciens identifiants de contexte vers les nouveaux.
// Les profils créés avant la simplification pointent encore vers ces anciens
// ids ; on les rattache automatiquement au bon nouvel item à l'affichage, et
// l'enregistrement suivant persiste le nouvel id (migration transparente).
const LEGACY_MEETING_CONTEXT_MAP: Record<string, string> = {
  educatif: 'ecole-universite',
  formation: 'ecole-universite',
  etude: 'ecole-universite',
  event: 'evenement-pro',
  'event-pro': 'evenement-pro',
  networking: 'evenement-pro',
  'etude-client': 'travail',
  sport: 'sport-activite',
  loisir: 'sport-activite',
  festival: 'evenement-social',
  artistique: 'evenement-social',
  'naturel-voyage': 'voyage',
  mariage: 'evenement-social',
  'rencontre-sociale': 'evenement-social',
  'rencontre-famille': 'famille',
  'rencontre-benevole': 'evenement-social',
  'famille-proche': 'famille',
  eventSpecific: 'evenement-social',
}

const VALID_MEETING_CONTEXT_IDS = new Set(contextItems.map((item) => item.id))

// Convertit une liste d'ids de contexte (potentiellement anciens) en nouveaux
// ids valides, en supprimant les doublons et les ids inconnus.
export const migrateMeetingContextIds = (ids: string[] | undefined | null): string[] => {
  if (!Array.isArray(ids)) return []
  const migrated = ids.map((id) => LEGACY_MEETING_CONTEXT_MAP[id] ?? id)
  const seen = new Set<string>()
  return migrated.filter(
    (id) => VALID_MEETING_CONTEXT_IDS.has(id) && !seen.has(id) && seen.add(id),
  )
}

interface ContextStore {
  contexts: GroupedItem[]
  getAllContexts: () => SelectionItem[]
  getContextsByGroup: (groupId: string) => SelectionItem[]
}

export const useContextStore = create<ContextStore>((set, get) => ({
  contexts: contextGrouped,
  getAllContexts: () => {
    // Flatten all items and remove duplicates by id
    const all = get().contexts.flatMap(group => group.items)
    const unique: { [id: string]: SelectionItem } = {}
    all.forEach(item => { unique[item.id] = item })
    return Object.values(unique)
  },
  getContextsByGroup: (groupId) => {
    const group = get().contexts.find(g => g.id === groupId)
    return group ? group.items : []
  },
}))

export const useTranslatedContextStore = () => {
  const { t } = useLanguage()
  const store = useContextStore()

  const translateGroup = (group: GroupedItem): GroupedItem => ({
    ...group,
    name: group.name ? t(group.name) : '',
    description: group.description ? t(group.description) : undefined,
    items: group.items.map(item => ({ ...item, name: t(item.name) })),
  })

  return {
    contexts: store.contexts.map(translateGroup),
    getAllContexts: () => store.getAllContexts().map(item => ({ ...item, name: t(item.name) })),
    getContextsByGroup: (groupId: string) => store.getContextsByGroup(groupId).map(item => ({ ...item, name: t(item.name) })),
  }
}
